import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  Toolbar,
  LinearProgress,
  Collapse,
  SxProps,
  Theme,
} from '@mui/material';

import { useAppSelector } from '../../../hooks';
import { getAuthState } from '../../../store/AuthSlice';
import { getIsLoading } from '../../../store/LoaderSlice';
import { checkUserPermission } from '../../../helper';
import AdminQuestionBank from '../QuestionBank';
import Subtopics from '../Subtopics';
import ManageQuestionsHome from '../ManageQuestions';
import ManageQuizzes from '../ManageQuizzes';
import ViewQuiz from '../ManageQuizzes/ViewQuiz';
import Students from '../Students';
import StudentSubmissions from '../Students/StudentSubmissions';
import ViewSubmissionQuestions from '../Students/ViewSubmissionQuestions';
import InvalidRoute from '../../Validate/InvalidRoute';
import Header from './components/Header';
import Drawer from './modals/Drawer';
import Questions from '../Questions';
import Diagnostic from '../../UserPages/Diagnostic';

// STLYES
const loaderStyles: SxProps<Theme> = {
  position: 'fixed',
  top: { xs: '56px', sm: '64px' },
  zIndex: 1,
  width: '100%',
};

const InvalidView = <InvalidRoute />;

const AdminDashboard: React.FC = () => {
  // State
  const [isDrawerOpen, toggleDrawer] = useState(false);

  // Store
  const authState = useAppSelector(getAuthState);
  const isLoading = useAppSelector(getIsLoading);

  const authUser = authState.user;

  // Permissions
  const subTopicViewPermission = checkUserPermission(authUser, {
    adminSubTopic: ['view'],
  });

  const questionsViewPermission = checkUserPermission(authUser, {
    adminQuestion: ['view'],
  });

  const quizViewPermission = checkUserPermission(authUser, {
    adminQuiz: ['view'],
  });

  const adminStudentPermission = checkUserPermission(authUser, {
    adminStudent: ['view'],
  });

  let defaultRoute = 'question-bank';

  if (authUser && authUser.role && authUser.role.name === 'Tutor') {
    defaultRoute = 'students';
  }

  return (
    <div>
      <Header toggleDrawer={() => toggleDrawer(true)} />
      <Toolbar />
      <Collapse in={isLoading} sx={loaderStyles}>
        <LinearProgress color="info" />
      </Collapse>
      <main style={{ minHeight: 'calc(100vh - 64px)' }}>
        <Routes>
          <Route path="/" element={<Navigate to={defaultRoute} />} />
          {/* Questions */}
          {questionsViewPermission && (
            <Route path="questions" element={<Questions />} />
          )}
          {questionsViewPermission && (
            <Route
              path="question-bank"
              element={<AdminQuestionBank type="question" />}
            />
          )}
          {subTopicViewPermission && (
            <Route
              path="question-bank/sub-topics/:subjectCode"
              element={<Subtopics />}
            />
          )}
          {questionsViewPermission && (
            <Route
              path="question-bank/questions/:subTopicId"
              element={<ManageQuestionsHome />}
            />
          )}
          {/* Quizzes */}
          {quizViewPermission && (
            <>
              <Route
                path="quiz-bank"
                element={<AdminQuestionBank type="quiz" />}
              />
              <Route
                path="quiz-bank/quizzes/:subjectCode"
                element={<ManageQuizzes />}
              />
              <Route
                path="quiz-bank/questions/:quizId"
                element={<ViewQuiz />}
              />
            </>
          )}
          {/* Students */}
          {adminStudentPermission && (
            <>
              <Route path="students" element={<Students />} />
              <Route
                path="students/submissions/:userId"
                element={<StudentSubmissions />}
              />
              <Route
                path="students/viewQuestions/:userId/:submissionId"
                element={<ViewSubmissionQuestions />}
              />
              <Route
                path="my-quizzes/diagnostic/:subtopicId/:diagnosticId/:pattern"
                element={<Diagnostic />}
              />
            </>
          )}
          {/* Validate */}
          <Route path="*" element={InvalidView} />
        </Routes>
      </main>
      {/* Drawer */}
      <Drawer open={isDrawerOpen} toggle={() => toggleDrawer(!isDrawerOpen)} />
    </div>
  );
};

export default AdminDashboard;
